import { Caption1, Title2, makeStyles, mergeClasses, tokens } from '@fluentui/react-components';
import { SPACE } from '../styles/scale';
import { useSurfaceStyles } from '../styles/surfaces';

/**
 * A summary counter — a number over a word (wireframe sheet 01, R1).
 *
 * ── These are filters, not decoration ──
 * R1 note 2: selecting a tile applies that status filter to the table below and
 * marks the tile selected; selecting it again clears it. That is a toggle
 * button, which is why the interactive form is a real `<button>` carrying
 * `aria-pressed` rather than a `<div>` with a click handler. A tile with no
 * `onSelect` is genuinely static — it renders no button at all, so nothing
 * promises an interaction that does not exist, and the hover lift
 * (`cardInteractive`) is withheld for the same reason.
 *
 * ── Two verified defects this exists to fix, both seen on screen ──
 * 1. The Dashboard's own counters marked "selected" with `colorBrandStroke1`
 *    plus `colorBrandBackground2` and nothing else. `teamsHighContrastTheme`
 *    maps every `colorBrand*` onto a Windows system colour, so the selected
 *    tile was indistinguishable from the other three. The selected state here
 *    is carried by `useSurfaceStyles().cardSelected` — which pairs the brand
 *    edge with a border-WIDTH change to `strokeWidthThick`, a signal that
 *    survives the hue being discarded — and by `aria-pressed` for assistive
 *    tech. Colour is the accelerant, never the carrier.
 * 2. A native `<button>` does NOT inherit `color` from `FluentProvider` the way
 *    a `<div>` does: `Title2`/`Caption1` set no colour of their own and rely on
 *    inheriting one, so the button fell back to the browser's `buttontext`
 *    system colour. That happened to stay legible in light and dark by
 *    coincidence and collided with high contrast's pure-black background — the
 *    tiles rendered as completely empty boxes (CLAUDE.md mistake-log entry 54).
 *    `colorNeutralForeground1` is therefore set explicitly on the tile, and its
 *    regression test reads the injected CSS rule rather than the rendered text,
 *    because a colour bug cannot touch text content.
 */

export interface StatTileProps {
  value: number;
  label: string;
  selected?: boolean;
  /** Absent ⇒ a static tile: no button, no hover lift, no pressed state. */
  onSelect?: () => void;
}

/**
 * A button's accessible name defaults to its concatenated text, which for this
 * layout is "3Pending review" — a number welded to a word, with no unit. The
 * name is composed instead so the tile announces what it counts.
 */
export function statTileAccessibleName(label: string, value: number): string {
  return `${label}, ${value} ${value === 1 ? 'request' : 'requests'}`;
}

const useStyles = makeStyles({
  /**
   * Layout only — the frame (background, border, radius, shadow, padding)
   * comes from the shared surface language, so a stat tile and a card cannot
   * drift apart. `boxSizing` because a native `<button>` is content-box by
   * default and would otherwise overflow its grid track once padded.
   */
  tile: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: SPACE.s,
    boxSizing: 'border-box',
    width: '100%',
    textAlign: 'left',
    color: tokens.colorNeutralForeground1,
  },

  /** Below the single breakpoint every touch target is at least 44px. */
  interactive: {
    '@media (max-width: 700px)': { minHeight: '44px' },
  },

  /** De-emphasis is a foreground token, never `opacity`. */
  label: { color: tokens.colorNeutralForeground2 },
});

export function StatTile({ value, label, selected = false, onSelect }: StatTileProps) {
  const styles = useStyles();
  const surfaces = useSurfaceStyles();

  // Title2 is reserved app-wide for exactly this: a stat-tile number.
  const body = (
    <>
      <Title2>{value}</Title2>
      <Caption1 className={styles.label}>{label}</Caption1>
    </>
  );

  if (!onSelect) {
    return (
      <div className={mergeClasses(surfaces.card, styles.tile, selected && surfaces.cardSelected)}>
        {body}
      </div>
    );
  }

  return (
    <button
      type="button"
      aria-pressed={selected}
      aria-label={statTileAccessibleName(label, value)}
      onClick={onSelect}
      className={mergeClasses(
        surfaces.card,
        surfaces.cardInteractive,
        styles.tile,
        styles.interactive,
        // Last, so its thicker border wins over the resting card's hairline.
        selected && surfaces.cardSelected,
      )}
    >
      {body}
    </button>
  );
}
